import { Money } from "@redotech/money";
import * as z from "zod/v4";
import {
  computeStagecoachOptimalPrice,
  StagecoachInferenceFeatureScaling as RuntimeStagecoachInferenceFeatureScaling,
  STAGECOACH_FEATURE_NAMES,
} from "../stagecoach-inference";
import { CoverageMlModel, STAGECOACH_COVERAGE_ML_MODEL_ID } from "../types";

const stagecoachInferenceFeatureScalingSchema_ = z.object({
  mean: z.number(),
  std: z.number(),
});
export const stagecoachInferenceFeatureScalingSchema: z.ZodType<StagecoachInferenceFeatureScaling> =
  stagecoachInferenceFeatureScalingSchema_;
export interface StagecoachInferenceFeatureScaling
  extends RuntimeStagecoachInferenceFeatureScaling,
    z.infer<typeof stagecoachInferenceFeatureScalingSchema_> {}

const stagecoachPosteriorSubsampleEntrySchema_ = z.object({
  betaSampleIndex: z.number().int().nonnegative(),
  betaVector: z.array(z.number()),
});
export const stagecoachPosteriorSubsampleEntrySchema: z.ZodType<StagecoachPosteriorSubsampleEntry> =
  stagecoachPosteriorSubsampleEntrySchema_;
export interface StagecoachPosteriorSubsampleEntry
  extends z.infer<typeof stagecoachPosteriorSubsampleEntrySchema_> {}

const stagecoachInferenceConfigSchema_ = z.object({
  stagecoachInferenceConfigVersion: z.number().int().positive(),
  modelVersionId: z.string(),
  // Detect-only: top-level betaVector/betaSampleIndex used directly by predict path.
  // PR6/7: replaced by posteriorSubsample; client resolves before calling predict.
  betaSampleIndex: z.number().int().nonnegative(),
  betaVector: z.array(z.number()),
  posteriorSubsample: z
    .array(stagecoachPosteriorSubsampleEntrySchema)
    .optional(),
  stagecoachFeatureOrdering: z.array(z.enum(STAGECOACH_FEATURE_NAMES)),
  featureComputationRules: z
    .object({
      priceRatioFormula: z.string().optional(),
      logCartValueEpsilon: z.number().positive().optional(),
      missingIntentStrategy: z.enum(["null", "zero", "global_mean"]).optional(),
    })
    .optional(),
  inferenceFeatureScaling: z
    .record(z.string(), stagecoachInferenceFeatureScalingSchema)
    .optional(),
  conversionVsPricingTradeoffConfig: z.object({ alpha: z.number() }).optional(),
  priceSearchConfig: z
    .object({ priceCandidateTotal: z.number().int().positive().optional() })
    .optional(),
});
export const stagecoachInferenceConfigSchema: z.ZodType<StagecoachInferenceConfig> =
  stagecoachInferenceConfigSchema_;
export interface StagecoachInferenceConfig
  extends z.infer<typeof stagecoachInferenceConfigSchema_> {}

const stagecoachCoverageMlModelDbSchema_ = z.object({
  id: z.literal(STAGECOACH_COVERAGE_ML_MODEL_ID),
  config: z
    .object({
      priceVsConversionWeight: z.number().nullish(),
      stagecoachInferenceConfig: stagecoachInferenceConfigSchema.nullish(),
    })
    .nullish(),
});

export const stagecoachCoverageMlModelDbSchema: z.ZodType<StagecoachCoverageMlModelDb> =
  stagecoachCoverageMlModelDbSchema_;

export interface StagecoachCoverageMlModelDb
  extends z.infer<typeof stagecoachCoverageMlModelDbSchema_> {}

type StagecoachCoverageMlModelConfig = {
  constraints: { minPrice: Money; maxPrice: Money };
  priceVsConversionWeight?: number | null;
  stagecoachInferenceConfig?: StagecoachInferenceConfig | null;
};

type StagecoachCoverageMlInferenceParams = {
  cartValue: number;
  checkoutProbability: number | undefined;
};

export const stagecoachCoverageMlModel: CoverageMlModel<
  StagecoachCoverageMlModelConfig,
  StagecoachCoverageMlInferenceParams
> = { id: STAGECOACH_COVERAGE_ML_MODEL_ID, predict };

function predict({
  modelConfig,
  inferenceParams,
}: {
  modelConfig: StagecoachCoverageMlModelConfig;
  inferenceParams: StagecoachCoverageMlInferenceParams;
}): { optimalPrice: Money } {
  const config = modelConfig.stagecoachInferenceConfig;
  if (
    !config ||
    config.betaVector.length === 0 ||
    config.betaVector.length !== config.stagecoachFeatureOrdering.length
  ) {
    return {
      optimalPrice: {
        amount: modelConfig.constraints.minPrice.amount,
        currency: modelConfig.constraints.minPrice.currency,
      },
    };
  }

  return {
    optimalPrice: computeStagecoachOptimalPrice({
      constraints: modelConfig.constraints,
      runtimeConfig: config,
      cartValue: inferenceParams.cartValue,
      checkoutProbability: inferenceParams.checkoutProbability,
      priceVsConversionWeight: modelConfig.priceVsConversionWeight,
    }),
  };
}
